import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  // A bookmarked admin page sends you here first; after signing in you land back on it instead of
  // on the dashboard home. Only in-app admin paths are honoured.
  const { state } = useLocation();
  const from =
    typeof state?.from === "string" && state.from.startsWith("/admin") ? state.from : "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      nav(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center px-5" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="display text-[26px] text-white">Human Home Living</div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-sage mt-1">Admin & CMS</div>
        </div>
        <form onSubmit={submit} className="bg-white px-8 py-9" data-testid="admin-login-form">
          <label className="block mb-5">
            <span className="text-[12px] tracking-[0.14em] uppercase text-[#6E6A63]">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="w-full border border-line px-4 py-3.5 mt-2 text-[15.5px] focus:outline-none focus:border-sage transition-colors"
            />
          </label>
          <label className="block mb-6">
            <span className="text-[12px] tracking-[0.14em] uppercase text-[#6E6A63]">Password</span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="w-full border border-line px-4 py-3.5 mt-2 text-[15.5px] focus:outline-none focus:border-sage transition-colors"
            />
          </label>
          {error && (
            <p data-testid="admin-login-error" className="text-[13.5px] text-[#C0392B] mb-5">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="w-full py-4 bg-charcoal text-white text-[12.5px] tracking-[0.16em] uppercase hover:bg-sage transition-colors duration-300 disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <Link
            to="/admin/forgot-password"
            data-testid="admin-forgot-link"
            className="block text-center mt-5 text-[13px] text-[#6E6A63] hover:text-charcoal"
          >
            Forgot your password?
          </Link>
        </form>
        <p className="text-center text-[12px] font-light text-[#A6A39D] mt-6">
          Private preview. Access is restricted to Human Home Living staff.
        </p>
      </div>
    </div>
  );
}
